import React, {useState, useEffect} from 'react'
import Stack from '@mui/material/Stack'
import type {Evaluation as EvalType, Model as ModelType} from '../../services/types'
import {getEvaluations} from '../../services/evaluations'
import {getModels} from '../../services/models'

import type {FiltersProps} from './types'
import {alarmCadences} from './constants'
import {filterChangeHandlers, handleRangeChange} from './transformers'
import {
  FiltersContainer,
  OptionsGroup,
  FilterAutocomplete,
  CheckboxGroup,
  RangeSlider,
  DatesPicker,
} from './widgets'

type TaggedEval = EvalType & {tag: string}
const AlarmFilters = ({config, onChange}: FiltersProps) => {
  const [evals, setEvals] = useState<TaggedEval[]>([])
  const [models, setModels] = useState<ModelType[]>([])
  const {handleChange, updateField} = filterChangeHandlers({
    config,
    onChange,
  })

  useEffect(() => {
    const getEvals = async () => {
      const spreadEvals = (e: EvalType): TaggedEval[] =>
        e.tags && e.tags.length > 0
          ? e.tags.map((tag) => ({...e, tag: tag.name}))
          : [{...e, tag: ''}]

      const evals = await getEvaluations({filters: {fields: ['name']}})
      setEvals(
        (evals.items as EvalType[])
          .reduce((acc, e) => [...acc, ...spreadEvals(e)], [] as TaggedEval[])
          .sort((a, b) =>
            a.tag === b.tag ? -b.name.localeCompare(a.name) : a.tag.localeCompare(b.tag)
          )
      )
    }
    getEvals()
    getModels({sort: {key: 'name', direction: 'Desc'}, filters: {fields: ['name']}}).then((m) =>
      setModels(m.items as ModelType[])
    )
  }, [])

  return (
    <FiltersContainer>
      <OptionsGroup label="Sources" sx={{width: 450, p: 1}}>
        <Stack direction="column" spacing={5} justifyContent="flex-start" alignItems="center">
          <FilterAutocomplete
            options={models.filter(({id}) => !config?.filters?.models?.some((i) => i?.id === id))}
            label="Filter models"
            onSelect={(m: ModelType) => updateField('models', m)}
          />
          <FilterAutocomplete
            options={evals.filter(({id}) => !config?.filters?.evals?.some((i) => i?.id === id))}
            label="Filter evaluations"
            onSelect={(e: TaggedEval) => updateField('evals', e)}
            groupBy={({tag}: TaggedEval) => tag}
          />
        </Stack>
      </OptionsGroup>
      <CheckboxGroup
        label="Cadendce"
        allOptions={alarmCadences}
        selected={config?.filters?.triggerCadence}
        onChange={handleChange('triggerCadence')}
      />
      <RangeSlider
        label="Threshold"
        start={config?.filters?.minThreshold || 0}
        end={config?.filters?.maxThreshold || 100}
        onChange={handleRangeChange(config?.filters, 'Threshold', updateField)}
      />
      <DatesPicker
        label="Created between"
        start={config?.filters?.startCreationDate}
        end={config?.filters?.endCreationDate}
        onChange={updateField}
        field="CreationDate"
      />
      <DatesPicker
        label="Next trigger between"
        field="PredictedTriggerDate"
        start={config?.filters?.startCreationDate}
        end={config?.filters?.endCreationDate}
        onChange={updateField}
      />
    </FiltersContainer>
  )
}

export default AlarmFilters
